import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

export interface Args {
	onClose: () => void;
}

export default class AppNotificationsVerifyComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('event-manager') declare eventManagerService: Service.EventManager;

	@tracked verifyIsResent = false;

	node: HTMLElement | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.eventManagerService.on(AppEvent.RESEND_VERIFY, this, () => {
			this.resendVerify();
		});
	}

	get email() {
		return this.currentUserService.model?.email;
	}

	override willDestroy(): void {
		super.willDestroy();
		this.eventManagerService.off(AppEvent.RESEND_VERIFY, this);
	}

	@action
	resendVerify() {
		this.verifyIsResent = true;
		void this.currentUserService.resendVerifyEmail();
	}

	@action
	close() {
		if (this.node) {
			$(this.node).fadeOut(1000);
		}

		later(() => {
			this.args.onClose();
		}, 1000);
	}

	@action
	contactUs() {
		this.currentUserService.chatWithSupport();
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node;
	}
	<template>
		<div
			{{didInsert this.setupView}}
			class={{joinClasses
				(array
					"notification-container"
					"verify-notification"
					(if this.verifyIsResent "verify-resent")
				)
			}}
		>
			<div class="container">

				<a role="button" class="close" {{on "click" this.close}}>
					<CommonUiIconComponent @name="Close" @size="sm" />
				</a>

				{{#if this.verifyIsResent}}
					<p>
						A confirmation link has been sent
						<span class="extra">
							to
							<strong>{{this.email}}</strong>
						</span>.
						<span class="extra">
							If you aren't getting the email,
							<a role="button" {{on "click" this.contactUs}}>get in touch with us</a>.
						</span>
					</p>
				{{else}}
					<CommonUiIconComponent @name="Mail" @size="md" @class="verify-icon" />
					<p>
						Your email address is not verified yet.
						<a role="button" {{on "click" this.resendVerify}}>
							Resend
							<span class="extra">verification email</span>
						</a>
					</p>
				{{/if}}
			</div>
		</div>
	</template>
}
